import { FC, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import { Calendar, isWindowPast, swedishHolidays } from '../../components/shared/Calendar';
import { deadlineRowsToEvents } from './todoCalendarAdapter';
import { ACTIVITY_COLORS, TYPE_LABELS } from './activityMeta';
import type { DeadlineRow, DeadlineActivityType, CalendarEvent } from './types';
import { loadStored, mergeStored, dropUnscopedKey } from '../../util/userScopedStorage';
import styles from './TodoCalendar.module.scss';

const CLOSED_MONTHS = 3;
const OPEN_MONTHS = 12;
const STORAGE_KEY = 'todo_calendar_view';

type Props = {
  rows: DeadlineRow[];
  selectedDates: Set<string>;
  onToggleDate: (iso: string) => void;
};

type YM = { year: number; month: number };

// Guard restored localStorage values — a malformed/partial YM would otherwise flow into
// moment()/buildMonthCells as NaN and blank the grid.
const isValidYM = (v: unknown): v is YM =>
  !!v && typeof v === 'object'
  && Number.isInteger((v as YM).year)
  && Number.isInteger((v as YM).month) && (v as YM).month >= 0 && (v as YM).month <= 11;

// Persist the range + expanded state across reloads (#4), per user.
const loadView = (): { closedStart?: YM; openStart?: YM; expanded?: boolean } => {
  dropUnscopedKey(STORAGE_KEY);
  const raw = loadStored(STORAGE_KEY);
  return {
    closedStart: isValidYM(raw.closedStart) ? raw.closedStart : undefined,
    openStart: isValidYM(raw.openStart) ? raw.openStart : undefined,
    expanded: typeof raw.expanded === 'boolean' ? raw.expanded : undefined,
  };
};

// Per-year cache so stepping the range doesn't recompute the Easter/fixed/Saturday holiday
// set for the same year on every prev/next click (#4).
const holidayCache = new Map<number, Set<string>>();
const holidaysForYear = (year: number): Set<string> => {
  let h = holidayCache.get(year);
  if (!h) {
    h = swedishHolidays(year);
    holidayCache.set(year, h);
  }
  return h;
};
const shift = (ym: YM, delta: number): YM => {
  const d = moment({ year: ym.year, month: ym.month, day: 1 }).add(delta, 'months');
  return { year: d.year(), month: d.month() };
};
const spanLabel = (start: YM, count: number) =>
  `${moment({ ...start, day: 1 }).format('MMM')} – ${moment({ ...start, day: 1 }).add(count - 1, 'months').format('MMM YYYY')}`;

const isInWindow = (iso: string, start: YM, count: number): boolean => {
  const from = moment({ year: start.year, month: start.month, day: 1 });
  const to = from.clone().add(count, 'months'); // exclusive
  const d = moment(iso, 'YYYY-MM-DD');
  return d.isSameOrAfter(from, 'day') && d.isBefore(to, 'day');
};

const inWindow = (evts: CalendarEvent[], start: YM, count: number): CalendarEvent[] =>
  evts.filter((e) => isInWindow(e.date, start, count));

const TodoCalendar: FC<Props> = ({ rows, selectedDates, onToggleDate }) => {
  const [t] = useTranslation('dashboard');

  const now = moment();
  const thisMonth: YM = { year: now.year(), month: now.month() };
  const saved = useMemo(loadView, []);
  // A saved range whose months have all passed is worse than no saved range: the page reopens on an
  // empty window and reads as broken rather than as remembered state. `expanded` is a preference
  // rather than navigation, so it survives regardless.
  const restore = (ym: YM | undefined, count: number): YM =>
    (ym && !isWindowPast(ym, count, thisMonth) ? ym : thisMonth);
  // Closed (3-month) and open (12-month) views keep INDEPENDENT range state — the open view
  // has its own filtering, as required. Initial values restore from localStorage (#4).
  const [closedStart, setClosedStart] = useState<YM>(() => restore(saved.closedStart, CLOSED_MONTHS));
  const [openStart, setOpenStart] = useState<YM>(() => restore(saved.openStart, OPEN_MONTHS));
  const [expanded, setExpanded] = useState(saved.expanded ?? false);

  // Skips the mount pass: writing on load would rewrite the record with only the fields this bundle
  // understands, before the user has changed anything. Combined with mergeStored, a version that
  // knows fewer fields now leaves the rest of the record alone instead of pruning it.
  const settled = useRef(false);
  useEffect(() => {
    if (!settled.current) { settled.current = true; return; }
    mergeStored(STORAGE_KEY, { closedStart, openStart, expanded });
  }, [closedStart, openStart, expanded]);

  const events = useMemo(() => deadlineRowsToEvents(rows, t), [rows, t]);
  const closedEvents = useMemo(() => inWindow(events, closedStart, CLOSED_MONTHS), [events, closedStart]);
  const openEvents = useMemo(() => inWindow(events, openStart, OPEN_MONTHS), [events, openStart]);
  const colorOf = (k: string) => ACTIVITY_COLORS[k as DeadlineActivityType]?.solid ?? '#94a3b8';
  const labelOf = (k: string) => {
    const pair = TYPE_LABELS[k as DeadlineActivityType];
    return pair ? t(pair[0], pair[1]) : k;
  };
  // Screen-reader name for a day cell — the full date plus how many activities it holds, so the
  // cell isn't announced as a bare "15". `count` is deliberate here (unlike the invariant "dgr"
  // badge): it makes i18next resolve the aktivitet/aktiviteter plural forms, looking up
  // calendar.dayLabel_one / _other rather than the bare key.
  // The fallbacks therefore have to be per-form too — a single defaultValue would be used for both
  // and render "1 aktiviteter" until Lokalise serves the keys.
  const dayLabel = (iso: string, count: number) =>
    t('calendar.dayLabel', {
      date: moment(iso, 'YYYY-MM-DD').format('D MMMM YYYY'),
      count,
      defaultValue_one: '{{date}}, {{count}} aktivitet',
      defaultValue_other: '{{date}}, {{count}} aktiviteter',
    });

  const stepper = (start: YM, setStart: (ym: YM) => void, count: number) => (
    <span className={styles.stepper}>
      <button type="button" className={styles.stepBtn} onClick={() => setStart(shift(start, -1))} aria-label={t('calendar.prevPeriod', 'Föregående')}>‹</button>
      <span>{spanLabel(start, count)}</span>
      <button type="button" className={styles.stepBtn} onClick={() => setStart(shift(start, 1))} aria-label={t('calendar.nextPeriod', 'Nästa')}>›</button>
    </span>
  );

  const months = expanded ? OPEN_MONTHS : CLOSED_MONTHS;
  const activeStart = expanded ? openStart : closedStart;
  const activeEvents = expanded ? openEvents : closedEvents;
  const activeSetter = expanded ? setOpenStart : setClosedStart;

  // The week number alone is ambiguous when the visible range straddles two ISO week-years — a
  // 12-month window usually contains the same number twice, so "v.30" does not say which one is
  // the peak. Only then is the year worth the extra characters in a badge this small.
  const spansTwoWeekYears = useMemo(() => {
    const from = moment({ year: activeStart.year, month: activeStart.month, day: 1 });
    const to = from.clone().add(months - 1, 'months').endOf('month');
    return from.isoWeekYear() !== to.isoWeekYear();
  }, [activeStart, months]);
  const peakLabel = (week: number, year: number) => (spansTwoWeekYears
    ? t('calendar.peakLabelWithYear', 'Högst tryck · v.{{week}} {{year}}', { week, year })
    : t('calendar.peakLabel', 'Högst tryck · v.{{week}}', { week }));

  // Navigating the range must NOT deselect dates — a selected day stays selected even when it
  // scrolls out of view; it's only cleared by clicking the date again or removing its filter chip.
  const common = { colorOf, labelOf, peakLabel, dayLabel, weekPrefix: t('calendar.weekPrefix', 'v.') };

  // Swedish red days across the year(s) the visible range spans (#11).
  const holidays = useMemo(() => {
    const endYear = moment({ year: activeStart.year, month: activeStart.month, day: 1 }).add(months - 1, 'months').year();
    const set = new Set<string>();
    for (let y = activeStart.year; y <= endYear; y++) holidaysForYear(y).forEach((d) => set.add(d));
    return set;
  }, [activeStart, months]);

  const atThisMonth = activeStart.year === thisMonth.year && activeStart.month === thisMonth.month;
  const rangeControl = (
    <span className={styles.rangeControl}>
      {!atThisMonth && (
        <button
          type="button"
          className={styles.todayBtn}
          onClick={() => activeSetter(thisMonth)}
        >
          {t('calendar.today', 'Idag')}
        </button>
      )}
      {stepper(activeStart, activeSetter, months)}
    </span>
  );

  return (
    <Calendar
      {...common}
      events={activeEvents}
      title={t('calendar.title', 'Kalender')}
      subtitle={t('calendar.subtitle', 'Kommande deadlines och aktiviteter')}
      rangeStart={activeStart}
      monthCount={months}
      selectedDates={selectedDates}
      onToggleDate={onToggleDate}
      rangeControl={rangeControl}
      heatLabels={{ calm: t('calendar.heat.calm', 'Lugnt'), busy: t('calendar.heat.busy', 'Högt tryck') }}
      holidays={holidays}
      expanded={expanded}
      onToggleExpand={() => setExpanded((v) => !v)}
      expandLabels={{
        expand: t('calendar.maximize', 'Maximera'),
        collapse: t('calendar.minimize', 'Minimera'),
      }}
    />
  );
};

export default TodoCalendar;
